import React from 'react';
import PropTypes from 'prop-types';
import { message, Timeline, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { etaPropTypes, geofencingDataPropTypes, stopsPropTypes } from '../../../types';
import { calculateEta } from '../../../store/actions';
import { isSuccess } from '../../../utils/responseUtils';
import TripStop from './TripStop';
import { Panel, StyledCollapse } from '../../../components';
import { stopsByTimeSlotAndSequence } from '../../../types/mappers';
import { getMonitoringOrderSnapshot } from '../../../store/selectors';

const getUnableToEstimateReason = (unableToEstimate) => `ORDER.ETA_FAILURE.${unableToEstimate.reasonCode}`;

/*
 * Takes two arguments and has only ever used two. The call site below passed four, silently
 * discarding `orderStatus` and `stop`.
 *
 * Wiring those two up - ORDER.ETA_FAILURE.ETA_DISABLED_STATUS and .ETA_DISABLED_STOP_PASSED are
 * already translated and clearly waiting for them - would disable the button on orders where it is
 * enabled today, which is a visible change to orders that carry no stopover labels at all. That is
 * out of bounds for this ticket, so the mismatch is resolved the other way: the dead arguments are
 * removed at the call site. Actually enabling those two conditions needs its own ticket.
 */
function getEtaDisabled(hasPosition, eta) {
  if (!hasPosition) {
    return { disabled: true, tooltipKey: 'ORDER.ETA_FAILURE.ETA_DISABLED_NO_POSITION' };
  }
  if (eta.loading) {
    return { disabled: true, tooltipKey: 'ORDER.ETA_FAILURE.ETA_DISABLED_LOADING' };
  }
  return { disabled: false, tooltipKey: null };
}

const TourDetailsCard = ({
  geofencingWithEtaEnabled,
  geofencingData,
  stops,
  orderId,
  shipperCompanyId,
  eta,
  selectedVehicle,
  hasPosition,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const monitoringOrderSnapshot = useSelector(getMonitoringOrderSnapshot);

  const onCalculateEta = (stop) => {
    dispatch(calculateEta({ orderId, shipperCompanyId, stopId: stop.id }))
      .then((action) => {
        if (isSuccess(action)) {
          const { unableToEstimate } = action.payload.data;
          const selectedNotEstimated = unableToEstimate
            && unableToEstimate.find(({ vehicleId }) => vehicleId === selectedVehicle);
          if (selectedNotEstimated) {
            message.error(t(getUnableToEstimateReason(selectedNotEstimated), 10));
          }
        }
      });
  };

  function getEtaForStop(stopId) {
    return eta.data[stopId] && eta.data[stopId].estimated
      && eta.data[stopId].estimated;
  }

  function hasGeofencingData(stopId) {
    if (geofencingData === null) {
      return false;
    }

    return !!geofencingData.stops
      .filter((stop) => stop.stopId === stopId)
      .flatMap((stop) => stop.assets).length;
  }

  function getGeofencingData(stopId) {
    if (!hasGeofencingData(stopId)) {
      return null;
    }

    return geofencingData.stops
      .filter((stop) => stop.stopId === stopId)
      .flatMap((stop) => stop.assets);
  }

  const hasMonitoringOrderSnapshot = monitoringOrderSnapshot !== null;

  function hasStopAssetSnapshotsData(stopId) {
    if (!hasMonitoringOrderSnapshot) {
      return false;
    }
    return !!monitoringOrderSnapshot.stopSnapshots
      .filter((stop) => stop.stopId === stopId)
      .flatMap((stop) => stop.assets).length;
  }

  function getStopAssetSnapshotsData(stopId) {
    if (!hasStopAssetSnapshotsData(stopId)) {
      return null;
    }

    return monitoringOrderSnapshot.stopSnapshots
      .filter((stop) => stop.stopId === stopId)
      .flatMap((stop) => stop.assets);
  }

  function getEtaStopLoading(stopId) {
    return eta.data[stopId] && eta.data[stopId].loading;
  }

  const resolveTitle = () => {
    const title = t('ORDER.TRIP_DETAILS');
    if (!hasMonitoringOrderSnapshot) {
      return (
        <>
          {`${title}`}
          <Tooltip title={`${t('ORDER.TRIP_DETAILS_ADDITIONAL_INFO_TOOLTIP_INFO')}`}>
            {` ${t('ORDER.TRIP_DETAILS_ADDITIONAL_INFO')} `}
            <InfoCircleOutlined />
          </Tooltip>
        </>
      );
    }
    return title;
  };

  return (
    <StyledCollapse defaultActiveKey={1}>
      <Panel header={resolveTitle()} key="1">
        <Timeline>
          {stops.sort(stopsByTimeSlotAndSequence)
            .map((stop, index) => (
              <TripStop
                geofencingWithEtaEnabled={geofencingWithEtaEnabled}
                hasGeofencingData={hasGeofencingData(stop.id)}
                geofencingData={getGeofencingData(stop.id)}
                stopAssetSnapshots={{
                  hasData: hasStopAssetSnapshotsData(stop.id),
                  data: getStopAssetSnapshotsData(stop.id),
                }}
                eta={{
                  onCalculate: onCalculateEta,
                  data: getEtaForStop(stop.id),
                  loading: getEtaStopLoading(stop.id),
                  ...getEtaDisabled(hasPosition, eta),
                }}
                stop={stop}
                index={index}
                key={stop.id}
              />
            ))}
        </Timeline>
      </Panel>
    </StyledCollapse>
  );
};

TourDetailsCard.propTypes = {
  stops: stopsPropTypes,
  orderId: PropTypes.string,
  shipperCompanyId: PropTypes.string,
  geofencingWithEtaEnabled: PropTypes.bool.isRequired,
  eta: etaPropTypes.isRequired,
  selectedVehicle: PropTypes.string,
  hasPosition: PropTypes.bool,
  geofencingData: geofencingDataPropTypes,
};

TourDetailsCard.defaultProps = {
  stops: null,
  orderId: null,
  selectedVehicle: null,
  shipperCompanyId: null,
  hasPosition: false,
  geofencingData: {},
};

export default TourDetailsCard;
